import classNames from '@utils/classnames';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { GearIcon, SealCheckIcon, TrashIcon, WarningCircleIcon, ClockIcon, TruckIcon } from '@phosphor-icons/react';

import { Card } from 'components/Card';
import { ActionMenuItem } from 'components/dialog/ActionMenuItem';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { ErrorState } from 'components/error-state/ErrorState';
import { ActionsMenu } from 'components/menu/ActionsMenu';
import { Pill } from 'components/pill/Pill';
import { SpinnerBlock } from 'components/Spinner';
import { useTranslation } from 'contexts/translation-context';
import { getDisplayError } from '@utils/get-display-error';
import { formatTimeSince } from '@utils/date';
import { getCountryFlag } from '@utils/country-flag';

import { LinkVehiclesDialog } from './LinkVehiclesDialog';
import {
  deleteTelemetryConnection,
  unlinkTelemetryVehicle,
  useTelemetryVehicles,
  verifyTelemetryConnection,
  type TelemetryConnection,
  type TelemetryParty,
  type TelemetryVehicle,
} from '../models';
import { labelOf, linkedNameOf } from '../vehicle-labels';

interface IConnectionCardProps {
  connection: TelemetryConnection;
  party?: TelemetryParty;
  /** Whether this caller may verify, delete or link — a driver may only look. */
  canWrite: boolean;
  onChanged: () => void;
  /** The vehicle whose panel the side column is showing, so its row reads as the picked one. */
  selectedVehicleId?: string | null;
  /** Pick that vehicle, or clear the panel with `null` — a unit linked to a truck or a trailer opens one. */
  onSelectVehicle?: (vehicle: TelemetryVehicle | null) => void;
}

/**
 * The flag for wherever a row's position line says the vehicle is — the linked truck's own country when it
 * has one, the payload's otherwise — so the flag and the place beside it come from one source.
 */
const flagFor = (vehicle: TelemetryVehicle): string =>
  getCountryFlag(vehicle.truckLocation?.country ?? vehicle.position?.countryCode);

/** One connection: its status, its actions, and the vehicles it reports with their links. */
export const ConnectionCard = ({
  connection,
  party,
  canWrite,
  onChanged,
  selectedVehicleId,
  onSelectVehicle,
}: IConnectionCardProps) => {
  const { i18n, lang } = useTranslation();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const { data: vehicles, error, isLoading, mutate } = useTelemetryVehicles(connection.id, party);

  const formatMoment = (value: string | null): string =>
    value
      ? new Date(value).toLocaleString(lang, { dateStyle: 'short', timeStyle: 'short' })
      : i18n('liveTracking.never');

  const owner =
    connection.ownerKind === 'Company' || connection.supplierId === null
      ? i18n('liveTracking.supplierOwnerCompany')
      : i18n('liveTracking.supplierOwner', { id: String(connection.supplierId) });

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const verified = await verifyTelemetryConnection(connection.id, party);
      if (verified.status === 'Active') {
        toast.success(i18n('liveTracking.verifyOk'));
      } else {
        toast.error(verified.lastError ?? i18n('liveTracking.verifyFailed'));
      }
      onChanged();
      await mutate();
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTelemetryConnection(connection.id, party);
      toast.success(i18n('liveTracking.connectionRemoved'));
      // This card is going away, so the panel that followed one of its vehicles goes with it.
      if (selectedVehicleId != null) {
        onSelectVehicle?.(null);
      }
      onChanged();
    } catch (err) {
      toast.error(getDisplayError(err));
    }
  };

  const handleUnlink = async (vehicle: TelemetryVehicle) => {
    try {
      await unlinkTelemetryVehicle(vehicle.id, party);
      toast.success(i18n('liveTracking.vehicleUnlinked'));
      await mutate();
      // The row that was showing its truck has just lost that link, so close the panel below the list.
      if (vehicle.id === selectedVehicleId) {
        onSelectVehicle?.(null);
      }
      onChanged();
    } catch (err) {
      toast.error(getDisplayError(err));
    }
  };

  const handleLinked = async () => {
    toast.success(i18n('liveTracking.vehicleLinked'));
    await mutate();
    onChanged();
  };

  /**
   * Where the vehicle is, as far as this list knows: the city and country are the linked truck's own, so
   * a vehicle still waiting to be linked shows the country the provider reported and nothing more.
   */
  const positionLine = (vehicle: TelemetryVehicle): string | null => {
    const position = vehicle.position;
    if (!position) {
      return null;
    }
    const place = vehicle.truckLocation
      ? [vehicle.truckLocation.city, vehicle.truckLocation.country].filter(Boolean).join(', ')
      : (position.countryCode ?? '');
    return [
      place,
      position.speed === null ? null : `${position.speed} km/u`,
      `${i18n('liveTracking.lastSignal')} ${formatMoment(vehicle.lastSeenAt)}`,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  return (
    <Card
      title={connection.label}
      subtitle={`${connection.provider} · ${owner}`}
      rightHeading={
        <>
          <Pill color={connection.status === 'Active' ? 'positive' : 'negative'} size="sm">
            {i18n(connection.status === 'Active' ? 'liveTracking.statusActive' : 'liveTracking.statusError')}
          </Pill>
          {/* Both counts are shorthand in the bar, spelled out in the tooltip. */}
          <Pill
            color="default"
            size="sm"
            title={i18n('liveTracking.linkedCount', {
              linked: String(connection.linkedCount),
              total: String(connection.vehicleCount),
            })}
          >
            <TruckIcon className="h-3 w-3 shrink-0" />
            {connection.linkedCount}/{connection.vehicleCount}
          </Pill>
          <Pill
            color="default"
            size="sm"
            title={`${i18n('liveTracking.lastChecked')} ${formatMoment(connection.lastVerifiedAt)}`}
          >
            <ClockIcon className="h-3 w-3 shrink-0" />
            {connection.lastVerifiedAt ? formatTimeSince(connection.lastVerifiedAt, lang) : i18n('liveTracking.never')}
          </Pill>
          {/* Linking a reported vehicle to a truck is a property of the connection, not of the row, so the
              whole of it — link and unlink — sits behind this one button rather than on every vehicle. */}
          {canWrite && vehicles != null && vehicles.length > 0 && (
            <LinkVehiclesDialog
              vehicles={vehicles}
              party={party}
              // A Suivo account's units are the trackers we install in trailers, so the target step offers
              // our trailers rather than the trucks a FleetMaster unit runs in.
              mode={connection.provider === 'Suivo' ? 'trailer' : 'truck'}
              onLinked={handleLinked}
              onUnlinked={handleUnlink}
            />
          )}
          {/* The connection's own controls live behind one button: they belong to this connection rather
              than to the page, and the header has no room for them all once a provider adds more. */}
          {canWrite && (
            <ActionsMenu
              label={i18n('liveTracking.connectionSettings')}
              icon={<GearIcon className="button-icon" />}
              header={
                <Pill color="default" size="sm">
                  {i18n('liveTracking.keyLabel')} ····{connection.credentialsLast4}
                </Pill>
              }
            >
              <ActionMenuItem
                isDisabled={isVerifying}
                iconLeft={<SealCheckIcon />}
                onTrigger={() => void handleVerify()}
              >
                {i18n('liveTracking.verify')}
              </ActionMenuItem>
              <ActionMenuItem isDanger iconLeft={<TrashIcon />} onTrigger={() => setIsRemoveOpen(true)}>
                {i18n('liveTracking.remove')}
              </ActionMenuItem>
            </ActionsMenu>
          )}
        </>
      }
    >
      {canWrite && (
        <ConfirmDialog
          isOpen={isRemoveOpen}
          onClose={() => setIsRemoveOpen(false)}
          title={i18n('liveTracking.removeTitle')}
          description={i18n('liveTracking.removeDescription', { label: connection.label })}
          submitText={i18n('liveTracking.remove')}
          onSubmit={handleDelete}
        />
      )}

      <div className="grid gap-4">
        {connection.lastError && (
          <div className="flex items-start gap-2 rounded-lg bg-negative-subtle px-3 py-2 text-sm text-negative-text">
            <WarningCircleIcon className="mt-0.5 w-4 h-4 shrink-0" weight="fill" />
            <span>{connection.lastError}</span>
          </div>
        )}

        {isLoading && !vehicles && <SpinnerBlock message={i18n('liveTracking.vehiclesLoading')} className="min-h-32" />}
        {error && !vehicles && <ErrorState error={error} onRetry={() => mutate()} />}

        {vehicles && vehicles.length === 0 && (
          <p className="text-sm text-fg-muted">{i18n('liveTracking.noVehicles')}</p>
        )}

        {vehicles && vehicles.length > 0 && (
          <ul className="divide-y divide-border">
            {vehicles.map((vehicle) => {
              const position = positionLine(vehicle);
              const flag = flagFor(vehicle);
              const isSelected = vehicle.id === selectedVehicleId;
              // A unit is linked to a truck or to a trailer — ours, for a trailer-tracker fleet — never both.
              const isLinked = Boolean(vehicle.supplierTruckId || vehicle.trailerId);
              // Only a linked unit has a panel to open: the truck's own, or the trailer's it tracks.
              const isSelectable = Boolean(isLinked && onSelectVehicle);

              const details = (
                <>
                  <div className="text-sm font-medium text-fg">{labelOf(vehicle)}</div>
                  <div className="text-sm text-fg-muted">
                    {isLinked
                      ? `${i18n('liveTracking.linkedTo', {
                          truck: linkedNameOf(vehicle),
                        })}${vehicle.linkSource === 'Manual' ? ` (${i18n('liveTracking.linkedManually')})` : ''}`
                      : i18n('liveTracking.notLinked')}
                  </div>
                  {position && (
                    <div className="text-sm text-fg-muted">
                      {flag && `${flag} `}
                      {position}
                    </div>
                  )}
                </>
              );

              return (
                <li key={vehicle.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                  {isSelectable ? (
                    // A raw button rather than the design system's `Button`: this is a whole list row and a
                    // 36px control is the wrong shape for one. The dev-login user cards are the same shape.
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => onSelectVehicle?.(isSelected ? null : vehicle)}
                      className={classNames(
                        '-mx-2 min-w-0 cursor-pointer rounded-md px-2 py-1 text-left transition-colors hover:bg-surface-hover',
                        isSelected && 'bg-brand-subtle',
                      )}
                    >
                      {details}
                    </button>
                  ) : (
                    <div className="min-w-0">{details}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
};
